import { useEffect, useState } from "react";
import { parseHexColorInput } from "../lib/brandColors";
import { cn } from "../lib/utils";
import { Input } from "./ui/input";

// Room for a pasted " #RRGGBB " — trimming happens in the parser, so the
// field must not truncate before it gets there.
export const HEX_INPUT_MAX_LENGTH = 12;

export const HEX_INPUT_INVALID_CLASS =
  "aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive";

// A 3-digit code is also the prefix of the 6-digit code being typed
// (`#ff0` on the way to `#ff0000`), so only a full 6-digit code applies
// while typing; shorthand applies when the field settles.
function isCompleteHexInput(text: string): boolean {
  return /^#?[0-9a-fA-F]{6}$/.test(text.trim());
}

/**
 * Draft state for a hex text field bound to a committed color.
 *
 * The text is free-form; `onChange` only ever receives a parsed `#RRGGBB`
 * (or `""` when `allowEmpty`), so partial input like `#ff` never reaches the
 * post. External changes (swatch, native picker, undo) replace the draft
 * unless the draft already spells that color (`#fff` while the value is
 * `#FFFFFF`).
 */
export function useHexColorDraft(
  value: string,
  onChange: (hex: string) => void,
  { allowEmpty = false }: { allowEmpty?: boolean } = {}
) {
  const [text, setText] = useState(value);
  const committed = parseHexColorInput(value);

  useEffect(() => {
    setText((current) =>
      committed && parseHexColorInput(current) === committed ? current : value
    );
  }, [value, committed]);

  const parsed = parseHexColorInput(text);
  const isEmpty = text.trim() === "";

  return {
    text,
    parsed,
    // A non-hex value the field was given (e.g. rgba) is not the user's typo.
    isInvalid: !isEmpty && !parsed && text !== value,
    change(next: string) {
      setText(next);
      if (isCompleteHexInput(next)) {
        onChange(parseHexColorInput(next)!);
      } else if (allowEmpty && next.trim() === "" && value !== "") {
        onChange("");
      }
    },
    /** Apply a valid draft (incl. shorthand) and tidy it; put back the value otherwise. */
    settle() {
      if (parsed) {
        if (parsed !== committed) onChange(parsed);
        setText(parsed);
      } else if (!(allowEmpty && isEmpty)) {
        setText(value);
      }
    },
  };
}

interface HexColorInputProps {
  value: string;
  onChange: (hex: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  /** Emptying the field commits `""` (e.g. to clear a per-run override). */
  allowEmpty?: boolean;
}

/**
 * Text field for a hex color that applies live once the text is a full
 * 6-digit code, and applies shorthand on blur/Enter. For fields with an
 * explicit Apply step, use `useHexColorDraft` directly and apply `parsed`.
 */
export function HexColorInput({
  value,
  onChange,
  className,
  placeholder = "#000000",
  allowEmpty,
  ...inputProps
}: HexColorInputProps) {
  const draft = useHexColorDraft(value, onChange, { allowEmpty });

  return (
    <Input
      {...inputProps}
      type="text"
      value={draft.text}
      onChange={(e) => draft.change(e.target.value)}
      onBlur={draft.settle}
      onKeyDown={(e) => {
        if (e.key === "Enter") draft.settle();
      }}
      aria-invalid={draft.isInvalid}
      placeholder={placeholder}
      maxLength={HEX_INPUT_MAX_LENGTH}
      spellCheck={false}
      autoComplete="off"
      className={cn("font-mono", HEX_INPUT_INVALID_CLASS, className)}
    />
  );
}
